import { Bell, ChevronDown, CreditCard, LogOut, Settings, User } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { useBranding } from '@/hooks/useBranding'
import { usePlanLimits } from '@/hooks/usePlanLimits'
import { authApi } from '@/api/auth'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatCredits } from '@/lib/utils'
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

export function Header() {
  const { user, logout, switchOrg, activeOrgId } = useAuth()
  const { brandName, brandFaviconUrl } = useBranding()
  const { balanceCredits, isCustomPricing } = usePlanLimits()
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false)
  const [userDropdownOpen, setUserDropdownOpen] = useState(false)

  // Update document title with brand name
  useEffect(() => {
    document.title = brandName
  }, [brandName])

  // Update favicon if custom one is set
  useEffect(() => {
    if (brandFaviconUrl) {
      const link: HTMLLinkElement =
        document.querySelector("link[rel~='icon']") || document.createElement('link')
      link.rel = 'icon'
      link.href = brandFaviconUrl
      document.head.appendChild(link)
    }
  }, [brandFaviconUrl])

  const { data: orgs = [] } = useQuery({
    queryKey: ['my-orgs'],
    queryFn: authApi.getMyOrgs,
    staleTime: 5 * 60 * 1000,
  })

  const currentOrg = orgs.find((o) => o.org_id === activeOrgId)

  return (
    <header className="flex h-16 items-center justify-between border-b bg-background px-6">
      {/*
        Impersonation banner is owned by ImpersonationBanner.tsx — it
        sits inside RequireAuth, so it renders above everything regardless
        of route. The duplicate banner that used to live here called
        useAuth() inside an onClick handler (hooks-rules violation) and
        was the source of an "Exit Impersonation" button that silently
        no-op'd in production. Don't reintroduce it.
      */}
      <div className="flex items-center gap-4">
        {/* Org Switcher */}
        <div className="relative">
          <button
            onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
            className="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent"
          >
            <div className="h-6 w-6 rounded bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
              {currentOrg?.org_name?.[0]?.toUpperCase() || 'O'}
            </div>
            <span className="font-medium">{currentOrg?.org_name || 'Select Organization'}</span>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </button>

          {orgDropdownOpen && (
            <div className="absolute left-0 top-full mt-1 w-72 rounded-md border bg-background shadow-lg z-50">
              <div className="p-2">
                <p className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase">
                  Your Organizations
                </p>
                {orgs.map((org) => (
                  <button
                    key={org.org_id}
                    onClick={() => {
                      switchOrg(org.org_id)
                      setOrgDropdownOpen(false)
                    }}
                    className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-accent ${
                      org.is_current ? 'bg-accent' : ''
                    }`}
                  >
                    <div className="h-6 w-6 rounded bg-primary/20 flex items-center justify-center text-xs font-bold">
                      {org.org_name[0].toUpperCase()}
                    </div>
                    <div className="text-left flex-1">
                      <div className="font-medium">{org.org_name}</div>
                      <div className="text-xs text-muted-foreground capitalize">{org.role.replace('org_', '')}</div>
                    </div>
                    {org.is_current && <Badge variant="secondary" className="text-xs">Active</Badge>}
                  </button>
                ))}
                <div className="my-1 h-px bg-border" />
                <Link
                  to="/orgs/new"
                  onClick={() => setOrgDropdownOpen(false)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-accent text-muted-foreground"
                >
                  + Create new organization
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        {/* Wallet Balance — hidden for custom pricing plans */}
        {!isCustomPricing && (
          <Link to="/billing" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <CreditCard className="h-3.5 w-3.5" />
            <span className="font-medium">{formatCredits(balanceCredits * 100)}</span>
          </Link>
        )}

        {/* User Menu */}
        <div className="relative">
          <button
            onClick={() => setUserDropdownOpen(!userDropdownOpen)}
            className="flex items-center gap-2 rounded-full p-1 hover:bg-accent"
          >
            <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-semibold">
              {user?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
            </div>
          </button>

          {userDropdownOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 rounded-md border bg-background shadow-lg z-50">
              <div className="p-2">
                <div className="px-2 py-1">
                  <p className="text-sm font-medium">{user?.full_name || 'User'}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <div className="my-1 h-px bg-border" />
                <Link
                  to="/settings"
                  onClick={() => setUserDropdownOpen(false)}
                  className="flex items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-accent"
                >
                  <Settings className="h-4 w-4" />
                  Settings
                </Link>
                <button
                  onClick={() => { logout(); setUserDropdownOpen(false) }}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-accent text-destructive"
                >
                  <LogOut className="h-4 w-4" />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
